import { ref } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useCart } from '~/composables/use-cart';
import { useCartDrawerVisibility } from '~/composables/use-cart-drawer-visibility';
import { TRACK_CART_SOURCE } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { updateCartContext } from '~/lib/dynamic-yield/dynamic-yield-context-manager';
import { sendDynamicYieldAddToCartEvent } from '~/lib/dynamic-yield/dynamic-yield-events';
import { removeCurrencySymbol } from '~/utils/currency';
import { extractCartSkus } from '~/utils/item-cart-properties';
import { parseVariantId } from '~/utils/variant-utils';

import { useViewportSize } from './use-viewport-size';

/**
 * Composable to drive master-card swatches via reactive state,
 * rather than manual DOM fiddling.
 *
 * @returns {{
 *   cardRefs: import('vue').Ref<Record<string, {
 *     selectedHandle: string,
 *     isSizeSelectorOpen: boolean,
 *     selectedUrl: string,
 *     selectedColorway: string,
 *     selectedColorwayId: string,
 *     selectedPrice: string,
 *     selectedCompareAtPrice: string,
 *     selectedImage: object,
 *     selectedVariantId: number,
 *     selectedVariantTitle: string,
 *     selectedIsFinalSale: boolean,
 *     isSizeSelected: boolean,
 *     isUnavailableSelected: boolean,
 *     currentVariants: Array<{id: number, title: string, available: boolean, product_id: number}>,
 *     colorwaySizeSelections: Record<string, {
 *       isSizeSelected: boolean,
 *       isUnavailableSelected: boolean,
 *       variantId: number,
 *       variantTitle: string
 *     }>
 *   }>>,
 *   addToCartWithoutEvent: (cardId: string) => Promise<void>,
 *   closeAllSizeSelectors: () => void,
 *   closeSizeSelector: (cardId: string) => void,
 *   handleAddToCart: (cardId: string) => Promise<void>,
 *   setActiveColorway: (cardId: string, event: Event) => void,
 *   setActiveVariant: (cardId: string, variantId: number, variantTitle: string, isAvailable: boolean, autoAddToCart?: boolean) => Promise<void>,
 *   toggleSizeSelector: (cardId: string) => void
 * }}
 */
export function useMasterProductCard() {
  const cardRefs = ref({});
  const { addSingleVariant } = useCart();
  const cartVisibility = useCartDrawerVisibility();
  const { isDesktop } = useViewportSize();

  /**
   * Initializes a card with default product data
   */
  function initializeCard(cardId, defaultData) {
    if (!cardRefs.value[cardId]) {
      cardRefs.value[cardId] = {
        currentVariants: defaultData.variants || [],
        isSizeSelected: false,
        isSizeSelectorOpen: false,
        isUnavailableSelected: false,
        selectedColorway: defaultData.colorway,
        selectedColorwayId: defaultData.colorwayId,
        selectedCompareAtPrice: defaultData.compareAtPrice,
        selectedHandle: defaultData.handle,
        selectedImage: defaultData.image,
        selectedIsFinalSale: defaultData.isFinalSale,
        selectedIsOnSale: defaultData.isOnSale,
        selectedMasterName: defaultData.masterName,
        selectedPrice: defaultData.price,
        selectedUrl: defaultData.url,
      };
    }
  }

  /**
   * Closes all size selectors when hovering outside any product card
   */
  function closeAllSizeSelectors() {
    Object.keys(cardRefs.value).forEach((cardId) => {
      if (cardRefs.value[cardId]?.isSizeSelectorOpen) {
        cardRefs.value[cardId] = {
          ...cardRefs.value[cardId],
          isSizeSelectorOpen: false,
        };
      }
    });
  }

  /**
   * Automatically initializes all product cards in a container
   * This eliminates the need for duplicate initialization code in each section
   */
  function initializeAllCards(container) {
    const productCards = container.querySelectorAll('[data-product-card]');
    productCards.forEach((card) => {
      const productId = card.getAttribute('data-product-id');

      // Get the default product data from the first swatch input
      const defaultSwatch = card.querySelector('input[type="radio"][checked]');
      if (defaultSwatch) {
        const variantsData = card.getAttribute('data-product-variants');
        const defaultColorwayId = defaultSwatch.getAttribute('data-product-colorway-id');
        const defaultVariants = getVariantsForColorway(variantsData, defaultColorwayId);

        const defaultData = {
          colorway: defaultSwatch.getAttribute('data-product-colorway'),
          colorwayId: defaultColorwayId,
          compareAtPrice: defaultSwatch.getAttribute('data-product-compare-at-price'),
          handle: defaultSwatch.getAttribute('data-product-handle'),
          image: JSON.parse(defaultSwatch.getAttribute('data-product-image')),
          isFinalSale: JSON.parse(
            defaultSwatch.getAttribute('data-product-is-final-sale') || 'false',
          ),
          isOnSale: JSON.parse(defaultSwatch.getAttribute('data-product-is-on-sale')),
          masterName: card.getAttribute('data-product-name'),
          price: defaultSwatch.getAttribute('data-product-price'),
          url:
            defaultSwatch.getAttribute('data-product-url') +
            (defaultSwatch.getAttribute('data-product-sale-params') || ''),
          variants: defaultVariants,
        };

        initializeCard(productId, defaultData);
      } else {
        // Handle cases where there are no swatch inputs (e.g., show_individual_colorways mode)
        // Get product data from the card's data attributes and product link
        const productLink = card.querySelector('a[data-product-link]');
        const productImage = card.querySelector('img[data-product-handle]');

        if (productLink && productImage) {
          // Extract price information from the price element properly
          const priceElement = card.querySelector('[data-product-colorway]')?.nextElementSibling;
          let priceText = '';
          let compareAtPriceText = '';
          let isOnSale = false;

          if (priceElement) {
            // Get the first span (sale price) and second span (compare at price) separately
            const priceSpans = priceElement.querySelectorAll('span');
            if (priceSpans.length >= 1) {
              priceText = priceSpans[0]?.textContent?.trim() || '';
            }
            if (priceSpans.length >= 2) {
              compareAtPriceText = priceSpans[1]?.textContent?.trim() || '';
              isOnSale = true;
            }
          }

          const variantsData = card.getAttribute('data-product-variants');
          const defaultVariants = getVariantsForColorway(variantsData, productId);

          const defaultData = {
            colorway: card.getAttribute('data-product-color') || '',
            colorwayId: productId,
            compareAtPrice: compareAtPriceText,
            handle: productImage.getAttribute('data-product-handle') || '',
            image: { src: productImage.src },
            isFinalSale: JSON.parse(card.getAttribute('data-is-final-sale') || 'false'),
            isOnSale: isOnSale,
            masterName: card.getAttribute('data-product-name') || '',
            price: priceText,
            url: productLink.href || '',
            variants: defaultVariants,
          };

          initializeCard(productId, defaultData);
        }
      }
    });

    // Initialize one-size products (auto-select the single variant)
    initializeOneSizeProducts(container);

    // Set up global mouse event listener to close size selectors when hovering outside
    const handleGlobalMouseMove = (event) => {
      const target = event.target;
      const isInsideCard = target.closest('[data-product-card]');

      if (!isInsideCard) {
        closeAllSizeSelectors();
      }
    };

    // Add event listener to the container
    container.addEventListener('mousemove', handleGlobalMouseMove);

    // Store the event listener for cleanup
    container._sizeSelectorMouseMoveHandler = handleGlobalMouseMove;
  }

  /**
   * Auto-selects the single variant for a one-size product
   */
  function autoSelectSingleVariant(cardId, colorwayId = null, variantsData) {
    if (!variantsData) return;

    try {
      const variants = JSON.parse(variantsData);

      if (!Array.isArray(variants) || variants.length === 0) return;

      let targetVariants = variants;

      // If colorwayId is provided, filter variants for that specific colorway
      if (colorwayId) {
        const colorwayIdNumber = parseInt(colorwayId);
        targetVariants = variants.filter((variant) => variant.product_id === colorwayIdNumber);
      }

      // If there's only one variant, auto-select it
      if (targetVariants.length === 1) {
        const singleVariant = targetVariants[0];
        setActiveVariant(
          cardId,
          singleVariant.id,
          singleVariant.title,
          singleVariant.available,
          false,
        );
      }
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useMasterProductCard - autoSelectSingleVariant - Error parsing variants data',
        { cardId, colorwayId, variantsData },
        error,
      );
    }
  }

  /**
   * Initializes one-size products by auto-selecting their single variant
   */
  function initializeOneSizeProducts(container) {
    const oneSizeCards = container.querySelectorAll('[data-product-one-size="true"]');
    oneSizeCards.forEach((card) => {
      const productId = card.getAttribute('data-product-id');

      // Early return if no product ID
      if (!productId) return;

      // Get variants data from the card
      const variantsData = card.getAttribute('data-product-variants');
      // Pass the productId as colorwayId to filter variants for the current colorway only
      autoSelectSingleVariant(productId, productId, variantsData);
    });
  }

  /**
   * Sets the active colorway for a product card
   */
  function setActiveColorway(cardId, colorwayId, event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const saleParams = input.dataset.productSaleParams;

    // Store the current variant selection for the current colorway before switching
    const currentColorwayId = cardRefs.value[cardId]?.selectedColorwayId;
    const currentVariantTitle = cardRefs.value[cardId]?.selectedVariantTitle;
    const currentVariantId = cardRefs.value[cardId]?.selectedVariantId;
    const currentIsSizeSelected = cardRefs.value[cardId]?.isSizeSelected;
    const currentIsUnavailableSelected = cardRefs.value[cardId]?.isUnavailableSelected;

    // Initialize colorwaySizeSelections if it doesn't exist
    if (!cardRefs.value[cardId]?.colorwaySizeSelections) {
      cardRefs.value[cardId] = {
        ...cardRefs.value[cardId],
        colorwaySizeSelections: {},
      };
    }

    // Store the current size selection for the current colorway
    if (currentColorwayId && currentVariantTitle) {
      cardRefs.value[cardId].colorwaySizeSelections[currentColorwayId] = {
        isSizeSelected: currentIsSizeSelected,
        isUnavailableSelected: currentIsUnavailableSelected,
        variantId: currentVariantId,
        variantTitle: currentVariantTitle,
      };
    }

    // Get variants for the new colorway
    const productCard = input.closest('[data-product-card]');
    const variantsData = productCard?.getAttribute('data-product-variants');
    const currentVariants = getVariantsForColorway(variantsData, colorwayId);

    // Update the card with new colorway data
    cardRefs.value[cardId] = {
      ...cardRefs.value[cardId],
      currentVariants: currentVariants,
      selectedColorway: input.dataset.productColorway,
      selectedColorwayId: colorwayId,
      selectedCompareAtPrice: input.dataset.productCompareAtPrice,
      selectedHandle: input.dataset.productHandle,
      selectedImage: JSON.parse(input.dataset.productImage),
      selectedIsFinalSale: JSON.parse(input.dataset.productIsFinalSale),
      selectedIsOnSale: JSON.parse(input.dataset.productIsOnSale),
      selectedPrice: input.dataset.productPrice,
      selectedUrl: `${input.dataset.productUrl}${saleParams}`,
    };

    // Check if we have a stored size selection for this colorway
    const storedSizeSelection = cardRefs.value[cardId].colorwaySizeSelections[colorwayId];

    if (storedSizeSelection) {
      // Restore the stored size selection
      cardRefs.value[cardId] = {
        ...cardRefs.value[cardId],
        isSizeSelected: storedSizeSelection.isSizeSelected,
        isUnavailableSelected: storedSizeSelection.isUnavailableSelected,
        selectedVariantId: storedSizeSelection.variantId,
        selectedVariantTitle: storedSizeSelection.variantTitle,
      };
    } else {
      // No stored selection, reset size selection
      cardRefs.value[cardId] = {
        ...cardRefs.value[cardId],
        isSizeSelected: false,
        isUnavailableSelected: false,
        selectedVariantId: null,
        selectedVariantTitle: null,
      };

      // Check if the new colorway is a one-size product and auto-select its variant
      if (productCard) {
        autoSelectSingleVariant(cardId, colorwayId, variantsData);
      }
    }
  }

  /**
   * Sets the active variant (size) for a product card
   */
  async function setActiveVariant(
    cardId,
    variantId,
    variantTitle,
    isAvailable,
    autoAddToCart = true,
  ) {
    cardRefs.value[cardId] = {
      ...cardRefs.value[cardId],
      isSizeSelected: true,
      isUnavailableSelected: !isAvailable,
      selectedVariantId: variantId,
      selectedVariantTitle: variantTitle,
    };

    // Automatically add to cart if the variant is available and autoAddToCart is true
    if (isAvailable && autoAddToCart) {
      await addToCartWithoutEvent(cardId);
    }
  }

  /**
   * Adds the selected product to cart without requiring an event
   */
  async function addToCartWithoutEvent(cardId) {
    const cardRef = cardRefs.value[cardId];
    if (!cardRef || !cardRef.isSizeSelected || cardRef.isUnavailableSelected) {
      return;
    }

    try {
      cartVisibility.openDrawer();
      cardRef.isSizeSelectorOpen = false;

      const itemCartProperties = {
        _color_name: cardRef.selectedColorway,
        _compare_at_price: removeCurrencySymbol(cardRef.selectedCompareAtPrice),
        _is_final_sale: cardRef.selectedIsFinalSale, // Default value since we don't have access to the button
        _master_name: cardRef.selectedMasterName || cardRef.selectedColorway,
      };

      let variantIdToAdd = cardRef.selectedVariantId;

      if (cardRef.selectedColorwayId && cardRef.selectedVariantTitle) {
        // Find the product card element to get variants data
        const productCards = document.querySelectorAll('[data-product-card]');
        let productCard = null;

        for (const card of productCards) {
          if (card.getAttribute('data-product-id') === cardId.toString()) {
            productCard = card;
            break;
          }
        }

        if (productCard) {
          const variantsData = productCard.getAttribute('data-product-variants');
          const parsedVariantId = parseVariantId(
            variantsData,
            cardRef.selectedVariantTitle,
            cardRef.selectedColorwayId,
          );

          if (parsedVariantId) {
            variantIdToAdd = parsedVariantId;
          }
        }
      }

      // Send Analytics for Quick Add, the product source is the AJAX Cart API product
      const updatedCart = await addSingleVariant(variantIdToAdd, 1, itemCartProperties);
      const productToAdd = updatedCart?.items.find((item) => item.id === variantIdToAdd);

      sendDynamicYieldAddToCartEvent({
        price: productToAdd.price / 100, // Convert cents to dollars
        productId: productToAdd.sku, // SKU exactly as it appears in product feed
        quantity: 1,
      });

      const cartSkus = extractCartSkus(updatedCart);
      updateCartContext(cartSkus);

      AnalyticsData.triggerProductAdded(
        updatedCart,
        productToAdd,
        TRACK_CART_SOURCE.QUICK_ADD,
        productToAdd.variantTitle,
      );
      return;
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useMasterProductCard - addToCartWithoutEvent - Error adding item to cart',
        {
          cardId,
          cardRef: cardRefs.value[cardId],
        },
        error,
      );
    }
  }

  /**
   * Gets variants for a specific colorway from the variants data
   */
  function getVariantsForColorway(variantsData, colorwayId) {
    if (!variantsData) return [];

    try {
      const variants = JSON.parse(variantsData);

      if (!Array.isArray(variants)) return [];

      // Convert colorwayId to number for comparison since variant.product_id is a number
      const colorwayIdNumber = parseInt(colorwayId, 10);

      return variants.filter((variant) => variant.product_id === colorwayIdNumber);
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useMasterProductCard - getVariantsForColorway - Error parsing variants data',
        { colorwayId, variantsData },
        error,
      );
      return [];
    }
  }

  /**
   * Gets the variant label (main part of the title) for display
   */
  function getVariantLabel(variantTitle, isUnisex = false) {
    if (!variantTitle) return '';

    if (isUnisex && variantTitle.includes('(')) {
      return variantTitle.split(' (')[0].trim().replace('UK ', '');
    }

    return variantTitle.replace('UK ', '');
  }

  /**
   * Gets the variant description (parenthetical part) for display
   */
  function getVariantDescription(variantTitle, isUnisex = false) {
    if (!variantTitle || !isUnisex || !variantTitle.includes('(')) {
      return '';
    }

    const parts = variantTitle.split(' (');
    if (parts.length > 1) {
      return parts[1].replace(')', '').trim().replace('UK ', '');
    }

    return '';
  }

  /**
   * Handles adding the selected product to cart
   */
  async function handleAddToCart(cardId, event) {
    const cardRef = cardRefs.value[cardId];
    if (!cardRef || !cardRef.isSizeSelected || cardRef.isUnavailableSelected) {
      return;
    }

    const button = event.target;

    if (!button) {
      return;
    }

    const isFinalSale = cardRef.selectedIsFinalSale;
    const masterName = button.getAttribute('data-product-master-name');

    try {
      cartVisibility.openDrawer();
      cardRef.isSizeSelectorOpen = false;

      const itemCartProperties = {
        _color_name: cardRef.selectedColorway,
        _compare_at_price: removeCurrencySymbol(cardRef.selectedCompareAtPrice),
        _is_final_sale: isFinalSale,
        _master_name: masterName,
      };

      let variantIdToAdd = cardRef.selectedVariantId;

      if (cardRef.selectedColorwayId && cardRef.selectedVariantTitle) {
        const productCard = button.closest('[data-product-card]');
        const variantsData = productCard?.getAttribute('data-product-variants');

        const parsedVariantId = parseVariantId(
          variantsData,
          cardRef.selectedVariantTitle,
          cardRef.selectedColorwayId,
        );

        if (parsedVariantId) {
          variantIdToAdd = parsedVariantId;
        }
      }

      // Send Analytics for Quick Add, the product source is the AJAX Cart API product
      const updatedCart = await addSingleVariant(variantIdToAdd, 1, itemCartProperties);
      const productToAdd = updatedCart?.items.find((item) => item.id === variantIdToAdd);

      sendDynamicYieldAddToCartEvent({
        price: productToAdd.price / 100, // Convert cents to dollars
        productId: productToAdd.sku, // SKU exactly as it appears in product feed
        quantity: 1,
      });

      const cartSkus = extractCartSkus(updatedCart);
      updateCartContext(cartSkus);

      AnalyticsData.triggerProductAdded(
        updatedCart,
        productToAdd,
        TRACK_CART_SOURCE.QUICK_ADD,
        productToAdd.variantTitle,
      );
      return;
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useMasterProductCard - handleAddToCart - Error adding item to cart',
        {
          cardId,
          cardRef: cardRefs.value[cardId],
          isFinalSale,
          masterName,
        },
        error,
      );
    }
  }

  function toggleSizeSelector(cardId) {
    if (isDesktop.value) {
      // Close all other size selectors first
      Object.keys(cardRefs.value).forEach((id) => {
        if (id !== cardId && cardRefs.value[id]?.isSizeSelectorOpen) {
          cardRefs.value[id] = {
            ...cardRefs.value[id],
            isSizeSelectorOpen: false,
          };
        }
      });

      // Toggle the current card's size selector
      cardRefs.value[cardId] = {
        ...cardRefs.value[cardId],
        isSizeSelectorOpen: !cardRefs.value[cardId].isSizeSelectorOpen,
      };
    }
  }

  /**
   * Closes the size selector for a product card
   */
  function closeSizeSelector(cardId) {
    cardRefs.value[cardId] = {
      ...cardRefs.value[cardId],
      isSizeSelectorOpen: false,
    };
  }

  return {
    addToCartWithoutEvent,
    cardRefs,
    closeAllSizeSelectors,
    closeSizeSelector,
    getVariantDescription,
    getVariantLabel,
    handleAddToCart,
    initializeAllCards,
    setActiveColorway,
    setActiveVariant,
    toggleSizeSelector,
  };
}
